import { Component, OnInit, OnDestroy } from '@angular/core';
import { ImageService } from 'src/app/services/image.service';
import { SafeUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { AppService } from 'src/app/services/app.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Icons } from 'src/app/models/icons.model';
import { WebKitDocument } from 'src/app/models/webkit.model';

@Component({
  selector: 'app-camera-viewer',
  standalone: false,
  templateUrl: './camera-viewer.component.html',
  styleUrls: ['./camera-viewer.component.scss'],
})
export class CameraViewerComponent implements OnInit, OnDestroy {
  icons = new Icons();
  imageSrc: SafeUrl;
  loadingMsg: string;

  get isFullscreen(): boolean {
    return (
      !!document.fullscreenElement ||
      (<WebKitDocument>document).webkitIsFullScreen
    );
  }

  private appLoadedHandler: Subscription = null;
  private appImageRefreshHandler: Subscription = null;
  private imageLoadingHandler: Subscription = null;
  private imageLoadedHandler: Subscription = null;

  constructor(
    private imageService: ImageService,
    public appService: AppService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.appImageRefreshHandler = this.appService.appRefreshImageEvent.subscribe(
      () => {
        this.loadingMsg = `Refreshing: ${this.appService.selectedCamera.Name}`;
        this.getImage();
      }
    );
  }

  ngOnInit(): void {
    if (!this.appService.isAppLoaded)
      this.appLoadedHandler = this.appService.appLoadedEvent.subscribe(() => {
        this.getRoute();
      });
    else this.getRoute();
  }

  /**
   * Exit fullscreen mode if currently in fullscreen. Uses standard and WebKit-prefixed methods for cross-browser compatibility.
   */
  exitFullscreen() {
    if (document.exitFullscreen) document.exitFullscreen();
    else if ((<WebKitDocument>document).webkitCancelFullScreen)
      (<WebKitDocument>document).webkitCancelFullScreen();
    else if ((<WebKitDocument>document).mozCancelFullScreen)
      (<WebKitDocument>document).mozCancelFullScreen();
  }

  /**
   * Subscribe to route parameters and update selected site and camera accordingly. If cameraId is not provided or invalid, defaults to the first camera of the site. If siteId is not provided, clears selected site and camera.
   */
  getRoute() {
    this.route.paramMap.subscribe((params) => {
      if (params.has('siteId')) {
        let siteId = +params.get('siteId');

        if (
          !this.appService.selectedSite ||
          this.appService.selectedSite.Id != siteId
        ) {
          this.appService.selectedSite = this.appService.sites.find(
            (s) => s.Id == siteId
          );
        }

        if (params.has('cameraId')) {
          let cameraId = +params.get('cameraId');

          this.appService.selectedCamera = this.appService.selectedSite.Cameras.find(
            (c) => c.Id == cameraId
          );

          // camera does not exist in this site
          if (!this.appService.selectedCamera) {
            this.appService.selectedCamera = this.appService.selectedSite.Cameras[0];
            this.router.navigate([
              '/view',
              this.appService.selectedSite.Id,
              this.appService.selectedCamera.Id,
            ]);
          }

          this.loadingMsg = `Loading: ${this.appService.selectedCamera.Name}`;
          this.getImage();
        } else {
          if (this.appService.selectedSite.Cameras.length > 0) {
            this.appService.selectedCamera = this.appService.selectedSite.Cameras[0];

            this.router.navigate([
              '/view',
              this.appService.selectedSite.Id,
              this.appService.selectedCamera.Id,
            ]);
          } else {
            this.appService.selectedCamera = null;
          }
        }
      } else {
        this.appService.selectedCamera = null;
        this.appService.selectedSite = null;
      }
    });
  }

  ngOnDestroy() {
    if (this.appLoadedHandler) this.appLoadedHandler.unsubscribe();
    if (this.appImageRefreshHandler) this.appImageRefreshHandler.unsubscribe();
  }

  /**
   * Fetches the image for the currently selected camera. Handles loading state and errors, updating the camera's error properties and the displayed image accordingly. If the camera has an error, clears the image source.
   */
  async getImage() {
    if (this.appService.selectedCamera) {
      if (this.appService.selectedCamera && !this.appService.selectedCamera.HasError) {
        this.appService.isImageLoading = true;
        this.appService.isImageLoaded = false;

        this.imageService
          .getImage(this.appService.selectedCamera.Id)
          .then((img) => {
            this.appService.selectedCamera.HasError = false;
            this.imageSrc = img;
          })
          .catch((err) => {
            if (err == 'timeout')
              this.appService.selectedCamera.ErrorMessage =
                'Camera image request timed out.';
            else {
              this.appService.selectedCamera.ErrorMessage = undefined;
              console.log(err);
            }
            this.appService.selectedCamera.HasError = true;
            this.imageSrc = null;
          })
          .finally(() => {
            this.appService.isImageLoaded = true;
            this.appService.isImageLoading = false;
          });
      } else {
        this.imageSrc = null;
      }
    }
  }
}
